/**
 * sl-srag.3.2: pure grouping + bounded-rendering model for QuickSearch docs
 * results.
 *
 * Keeps the result-shaping logic ("group by doc, cap visible places, expose a
 * load-more affordance") separate from the framework-scoped session that turns
 * the rows into `QuickSearchItem`s with icons / i18n / click handlers (SOLID).
 * This module is pure and serialisable, so the flood-protection contract is
 * unit-testable without a DOM or the DI container.
 *
 * Flood protection (a doc with many hits must never emit an unbounded list):
 * a doc lists PLACES_PER_DOC places and reports the hidden count so the
 * renderer can show an explicit "Show N more" row — no silent slicing.
 */
import type { SearchSourceType } from '@affine/core/modules/docs-search';

import { getDocsSearchLocationIdentity } from '../../docs-search/services/source-navigation';
import {
  type AggregatedPdfDoc,
  type AggregatedPdfPage,
  aggregatePdfHitsByDoc,
  type DocsSearchResult,
  filterBlockResultsShadowedByPdfHits,
  type PdfPageSearchHit,
} from './docs-pdf-search';

/** Places listed under one doc before its "Show N more" row. */
export const PLACES_PER_DOC = 3;

/** Stable load-more list key of one doc's places. */
export const docPlacesListKey = (docId: string) => `doc-places:${docId}`;

/**
 * Generic visible / hidden split with an explicit load-more contract: when a
 * list exceeds `limit` and is not force-expanded, show the first `limit` items
 * and report how many remain. `hidden === 0` means the whole list is shown.
 */
export function splitForLoadMore<T>(
  items: readonly T[],
  limit: number,
  showAll: boolean
): { visible: T[]; hidden: number } {
  if (showAll || items.length <= limit) {
    return { visible: [...items], hidden: 0 };
  }
  return { visible: items.slice(0, limit), hidden: items.length - limit };
}

interface RankedHit {
  score: number;
  rank?: number;
}

/**
 * Local hits first, the best score first; server hits below them in server
 * order. The local answer is shown first, so the server answer only appends.
 */
export function compareSearchHits(a: RankedHit, b: RankedHit): number {
  if (a.rank === undefined) {
    return b.rank === undefined ? b.score - a.score : -1;
  }
  return b.rank === undefined ? 1 : a.rank - b.rank;
}

export interface BlockDocGroup {
  docId: string;
  /** Highest-scoring hit for this doc; drives the doc's score. */
  best: DocsSearchResult;
  /** All hits for this doc, in arrival (score-desc) order, `best` included. */
  sources: DocsSearchResult[];
}

export interface SearchDocBucket {
  docId: string;
  score: number;
  /** Rank of the doc's best server hit; unset when a local hit leads. */
  rank?: number;
  sourceTypes: SearchSourceType[];
  pdf?: AggregatedPdfDoc;
  block?: BlockDocGroup;
}

/**
 * One top-level search result row per document. PDF page search, local block
 * search and remote database/doc search can all return hits for the same doc;
 * the UI must merge them here instead of rendering one top-level row per
 * backend source. This keeps local-first/remote merge semantics out of the
 * view layer.
 */
export function buildSearchDocBuckets(
  blockResults: readonly DocsSearchResult[],
  pdfHits: readonly PdfPageSearchHit[]
): SearchDocBucket[] {
  const buckets = new Map<string, SearchDocBucket>();

  for (const pdf of aggregatePdfHitsByDoc([...pdfHits])) {
    buckets.set(pdf.docId, {
      docId: pdf.docId,
      score: pdf.bestHit.score,
      sourceTypes: ['pdf'],
      pdf,
    });
  }

  const blockGroups = groupBlockResultsByDoc(
    dedupeBlockResultsByLocation(
      filterBlockResultsShadowedByPdfHits([...blockResults], [...pdfHits])
    )
  );
  for (const block of blockGroups) {
    const existing = buckets.get(block.docId);
    if (existing) {
      existing.block = block;
      existing.score = Math.max(existing.score, block.best.score);
      existing.rank = undefined;
      existing.sourceTypes = uniqueSourceTypes([
        ...existing.sourceTypes,
        ...block.sources.map(source => source.sourceType),
      ]);
    } else {
      buckets.set(block.docId, {
        docId: block.docId,
        score: block.best.score,
        rank: block.best.rank,
        sourceTypes: uniqueSourceTypes(
          block.sources.map(source => source.sourceType)
        ),
        block,
      });
    }
  }

  return [...buckets.values()].sort(compareSearchHits);
}

/**
 * Group flat block results by doc, preserving the incoming (score-descending)
 * order both across docs and within a doc. The first hit seen for a doc is its
 * `best` because the aggregate emits hits in score order.
 */
export function groupBlockResultsByDoc(
  results: readonly DocsSearchResult[]
): BlockDocGroup[] {
  const groups = new Map<string, BlockDocGroup>();
  for (const result of results) {
    const existing = groups.get(result.docId);
    if (existing) {
      existing.sources.push(result);
    } else {
      groups.set(result.docId, {
        docId: result.docId,
        best: result,
        sources: [result],
      });
    }
  }
  return [...groups.values()];
}

/**
 * Collapse multiple index rows that point at the same navigable search target.
 *
 * Search can return the same physical block/cell/edgeless element through
 * several sources (remote aggregate + local aggregate, or several snippets from
 * one indexed block). The UI can only navigate to the physical location, so
 * keeping each snippet as a separate row creates duplicate rows and duplicate
 * QuickSearch keys. Preserve the first row's order, keep the strongest score,
 * and use the strongest row's snippet/title when it wins.
 */
export function dedupeBlockResultsByLocation(
  results: readonly DocsSearchResult[]
): DocsSearchResult[] {
  const byLocation = new Map<string, DocsSearchResult>();

  for (const result of results) {
    const key = blockResultLocationKey(result);
    const existing = byLocation.get(key);
    if (!existing) {
      byLocation.set(key, result);
      continue;
    }

    if (compareSearchHits(result, existing) < 0) {
      byLocation.set(key, {
        ...existing,
        ...result,
        sourceType: existing.sourceType,
        databaseId: existing.databaseId ?? result.databaseId,
        databaseRowId: existing.databaseRowId ?? result.databaseRowId,
        databaseRowShardDocId:
          existing.databaseRowShardDocId ?? result.databaseRowShardDocId,
        databaseColumnId: existing.databaseColumnId ?? result.databaseColumnId,
        visibility: existing.visibility ?? result.visibility,
        technicalSourceKind:
          existing.technicalSourceKind ?? result.technicalSourceKind,
      });
      continue;
    }

    byLocation.set(key, {
      ...existing,
      ...(!existing.blockContent && result.blockContent
        ? { blockContent: result.blockContent }
        : {}),
      // Ranking decides presentation, never navigation identity. A local hit
      // can win by score/content while only the remote cell index knows the
      // exact row-shard document required for cross-shard fullscreen focus.
      databaseId: existing.databaseId ?? result.databaseId,
      databaseRowId: existing.databaseRowId ?? result.databaseRowId,
      databaseRowShardDocId:
        existing.databaseRowShardDocId ?? result.databaseRowShardDocId,
      databaseColumnId: existing.databaseColumnId ?? result.databaseColumnId,
      visibility: existing.visibility ?? result.visibility,
      technicalSourceKind:
        existing.technicalSourceKind ?? result.technicalSourceKind,
    });
  }

  return [...byLocation.values()];
}

function blockResultLocationKey(result: DocsSearchResult) {
  return getDocsSearchLocationIdentity(result);
}

/**
 * One place of a doc where the query was found: a PDF page (with the count of
 * hits on it) or an indexed block, canvas element or database cell. A doc is
 * the group the user may open; its places are listed under it, always in view.
 */
export type SearchDocPlace =
  | {
      kind: 'pdf-page';
      page: AggregatedPdfPage;
      /** 1-based position of the page's PDF among the doc's PDFs. */
      attachmentIndex: number;
      attachmentCount: number;
      score: number;
    }
  | { kind: 'block'; source: DocsSearchResult; score: number; rank?: number };

/** A hit on the doc itself (its title), not on a place inside it. */
export function isDocTitleHit(result: DocsSearchResult) {
  return (
    result.sourceType === 'doc' &&
    !result.blockId &&
    !result.elementId &&
    !result.databaseId
  );
}

/** The source kind of a place. */
export function searchDocPlaceSourceType(
  place: SearchDocPlace
): SearchSourceType {
  return place.kind === 'pdf-page' ? 'pdf' : place.source.sourceType;
}

/**
 * Every place of a doc, the best first; with `source`, only its places of that
 * kind (a search scoped to PDFs lists and counts the PDF pages alone).
 */
export function buildSearchDocPlaces(
  bucket: SearchDocBucket,
  source: SearchSourceType | null = null
): SearchDocPlace[] {
  const attachments = bucket.pdf?.attachments ?? [];
  const places: SearchDocPlace[] = [
    ...attachments.flatMap((attachment, index) =>
      attachment.pages.map(
        (page): SearchDocPlace => ({
          kind: 'pdf-page',
          page,
          attachmentIndex: index + 1,
          attachmentCount: attachments.length,
          score: page.bestHit.score,
        })
      )
    ),
    ...(bucket.block?.sources ?? [])
      .filter(source => !isDocTitleHit(source))
      .map(
        (source): SearchDocPlace => ({
          kind: 'block',
          source,
          score: source.score,
          rank: source.rank,
        })
      ),
  ];
  return places
    .filter(place => !source || searchDocPlaceSourceType(place) === source)
    .sort(compareSearchHits);
}

/** The doc's title as the search marked it, when a hit matched the title. */
export function markedDocTitle(bucket: SearchDocBucket): string | undefined {
  return bucket.block?.sources.find(source => source.title?.includes('<b>'))
    ?.title;
}

/** The source kinds of some places, e.g. the ones behind "Show N more". */
export function searchDocPlaceSourceTypes(
  places: readonly SearchDocPlace[]
): SearchSourceType[] {
  return uniqueSourceTypes(places.map(searchDocPlaceSourceType));
}

function uniqueSourceTypes(types: readonly SearchSourceType[]) {
  return [...new Set(types)];
}
